/**
 * Empty CMS shapes for editor state and API merge (ids fixed, content blank).
 * Live content always comes from MongoDB via the API.
 */
import {
  TRUST_BAR_IDS,
  FEATURE_IDS,
  STEP_IDS,
  ROLE_IDS,
  ABOUT_FEATURE_IDS,
  SOLUTIONS_SMART_IDS,
  SOLUTIONS_ALT_IDS,
  DEVICE_IDS,
  CONTACT_INFO_IDS,
  CONTACT_FIELD_IDS,
} from "./cmsIds";

const emptyImage = () => ({ src: "", alt: "" });

const STEP_NUMBERS = ["01", "02", "03", "04"];

export function createEmptyTranslations(translationKeys) {
  const keys = translationKeys || [];
  return Object.fromEntries(keys.map((key) => [key, ""]));
}

const emptyProgramCta = () => ({
  title: "",
  description: "",
  cta: { label: "", href: "" },
  image: emptyImage(),
});

const emptyMarketingHero = () => ({
  backgroundImage: "",
  headline: "",
  description: "",
  cta: { label: "", href: "" },
});

const emptyDeviceProduct = (id) => ({
  id,
  title: "",
  description: "",
  image: emptyImage(),
  detail: {
    title: "",
    image: emptyImage(),
    paragraphs: [""],
    specifications: [{ label: "" }],
  },
});

export function createEmptyHome() {
  return {
    hero: {
      backgroundImage: "",
      shadowImage: "",
      headline: { prefix: "", highlight: "", rest: "" },
      description: "",
      cta: { label: "", href: "" },
      arrowIcon: emptyImage(),
      heroImage: emptyImage(),
    },
    trustBar: {
      items: TRUST_BAR_IDS.map((id) => ({ id, title: "", image: emptyImage() })),
    },
    careChallenge: {
      challenge: { icon: "", title: "", paragraphs: ["", ""] },
      bridgeText: "",
      platform: { icon: "", title: "", paragraphs: ["", ""] },
    },
    features: {
      sectionTitle: "",
      sectionIcon: emptyImage(),
      items: FEATURE_IDS.map((id) => ({
        id,
        title: "",
        description: "",
        image: emptyImage(),
      })),
    },
    howItWorks: {
      sectionTitle: "",
      sectionIcon: emptyImage(),
      image: emptyImage(),
      steps: STEP_IDS.map((id, index) => ({
        id,
        number: STEP_NUMBERS[index] || String(index + 1).padStart(2, "0"),
        title: "",
        description: "",
      })),
    },
    roleExperience: {
      sectionTitle: "",
      sectionIcon: emptyImage(),
      roles: ROLE_IDS.map((id) => ({
        id,
        title: "",
        image: emptyImage(),
        points: [""],
      })),
    },
    security: {
      title: "",
      description: "",
      badges: ["", "", "", "", "", ""],
      image: emptyImage(),
      ctaBlock: emptyProgramCta(),
    },
  };
}

export function createEmptyAbout() {
  return {
    hero: emptyMarketingHero(),
    features: ABOUT_FEATURE_IDS.map((id, index) => ({
      id,
      title: "",
      imagePosition: index % 2 === 0 ? "left" : "right",
      image: emptyImage(),
      paragraphs: ["", ""],
    })),
    programCta: emptyProgramCta(),
  };
}

export function createEmptySolutions() {
  return {
    hero: emptyMarketingHero(),
    smartSolutions: {
      sectionTitle: "",
      sectionIcon: emptyImage(),
      items: SOLUTIONS_SMART_IDS.map((id) => ({
        id,
        title: "",
        description: "",
        icon: emptyImage(),
      })),
    },
    alternatingFeatures: SOLUTIONS_ALT_IDS.map((id, index) => ({
      id,
      title: "",
      imagePosition: index % 2 === 0 ? "right" : "left",
      background: "muted",
      bulletIconSrc: "",
      image: emptyImage(),
      paragraphs: [""],
      bullets: [""],
    })),
    integratedDevices: {
      sectionTitle: "",
      sectionIcon: emptyImage(),
      description: "",
      specCheckIcon: "",
      products: DEVICE_IDS.map((id) => emptyDeviceProduct(id)),
    },
    programCta: emptyProgramCta(),
  };
}

export function createEmptyContact() {
  return {
    hero: {
      backgroundImage: "",
      headline: "",
      description: "",
    },
    infoCards: CONTACT_INFO_IDS.map((id) => ({
      id,
      title: "",
      icon: "",
      value: "",
      detail: "",
      href: "",
    })),
    messageSection: {
      title: "",
      description: "",
      image: emptyImage(),
      form: {
        submitLabel: "",
        fields: CONTACT_FIELD_IDS.map((id) => ({
          id,
          name: id,
          label: "",
          type: id === "email" ? "email" : id === "mobile" ? "tel" : "text",
          placeholder: "",
          icon: "",
          autoComplete: "",
          required: id === "fullName" || id === "email",
        })),
      },
    },
  };
}
